import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import CustomButton from "../../../components/CustomButton";
import Pagination from "../../../components/CustomPagination";
import UserDetailsDrawer from "../../../components/Drawers/UserDetailsDrawer";
import { setDrawer } from "../../../redux-store/features/layoutSlice";
import {
  useGetSponsorMasterUsers,
  useGetSponsorMasterUserDetails,
} from "../../../custom-hooks/queries";
import SkeletonLoader from "../../../components/SkeletonLoader";
import UserActionDropdown from "../../../components/UserActionDropdown";
import { StatusBadges } from "../../../components/StatusBadges";

const SponsorMasterUserList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRow, setExpandedRow] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data: sponsorMasterUsers, isLoading } = useGetSponsorMasterUsers(
    21,
    currentPage
  );
  const { data: userDetails } = useGetSponsorMasterUserDetails(expandedRow);

  const handleExpandRow = (id) => {
    if (expandedRow === id) {
      setExpandedRow(null);
      dispatch(setDrawer(false));
    } else {
      setExpandedRow(id);
      dispatch(setDrawer(true));
    }
  };

  const formatUserDetails = (details) => {
    if (!details) return null;

    return {
      id: details.id,
      name: `${details.first_name} ${details.last_name}`,
      email: details.email,
      phone: details.phone_number,
      officePhone: details.office_phone_number,
      firstName: details.first_name,
      lastName: details.last_name,
      createdDate: details.create_date_time,
      updatedDate: details.update_date_time,
      status: details.is_active ? "Active" : "Inactive",
      contactPreference: details.contact_preference,
      sponsorId: details.sponsor_id,
    };
  };

  const handleAddNewAccount = () => {
    navigate("/Backend/Users/AddSponsorMasterUser");
  };

  const handleManageUser = (userId) => {
    navigate(`/Backend/Users/SponsorMasterUsers/${userId}/manage`);
  };

  const getStatusBadge = (isActive) => {
    return (
      <span
        className={`inline-flex items-center rounded-md ${
          isActive
            ? "bg-green-50 text-green-700 ring-green-600/20"
            : "bg-red-50 text-red-700 ring-red-600/10"
        } px-2 py-1 text-xs font-medium ring-1 ring-inset`}
      >
        {isActive ? "Active" : "Inactive"}
      </span>
    );
  };

  return (
    <div className="container w-full px-4 sm:px-6 lg:px-8 min-h-screen">
      <div className="border-b border-gray-200 px-5 py-5 flex items-center justify-between">
        <h1 className="font-semibold flex items-center">
          <span>Users</span>
          <div className="border-l border-gray-300 h-6 mx-3"></div>
          <span>Sponsor Master Users</span>
        </h1>
      </div>

      <div className="mt-8">
        <div className="flex justify-end items-center mb-5">
          <div style={{ marginTop: "10px", marginLeft: "10px" }}>
            <CustomButton
              color="bg-indigo-600"
              size="text-base"
              weight="font-normal"
              height="h-9"
              width="w-32"
              textColor="text-white"
              text="Add User"
              borderRadius="0.375rem"
              onClick={handleAddNewAccount}
            />
          </div>
        </div>

        {isLoading ? (
          <SkeletonLoader />
        ) : sponsorMasterUsers?.items?.length === 0 ? (
          <div className="flex flex-col items-center mt-10">
            <span className="p-3 rounded-full bg-custom-link-bg-color flex justify-center items-center">
              <MagnifyingGlassIcon
                width={20}
                height={20}
                style={{ color: "white" }}
              />
            </span>
            <p className="mt-3 text-lg text-gray-700 font-semibold">
              No users found
            </p>
            <p className="text-gray-500 mt-1 text-center">
              Your search did not match any users. Please try again.
            </p>
          </div>
        ) : (
          <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
            <div className="inline-block w-full py-2 align-middle sm:px-6 lg:px-1">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300 table-fixed">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="w-1/6 px-3 py-3.5 text-start text-sm font-semibold text-gray-900">
                        First Name
                      </th>
                      <th className="w-1/6 px-3 py-3.5 text-start text-sm font-semibold text-gray-900">
                        Last Name
                      </th>
                      <th className="w-1/6 px-3 py-3.5 text-start text-sm font-semibold text-gray-900">
                        Email Address
                      </th>
                      <th className="w-1/6 px-3 py-3.5 text-center text-sm font-semibold text-gray-900">
                        Status
                      </th>
                      <th className="w-1/6 px-3 py-3.5 text-end text-sm font-semibold text-gray-900">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sponsorMasterUsers?.items?.map((item) => (
                      <React.Fragment key={item.id}>
                        <tr className="border-b border-gray-200">
                          <td className="whitespace-break-words px-3 py-4 text-sm text-gray-500">
                            {item.first_name}
                          </td>
                          <td className="whitespace-break-words px-3 py-4 text-sm text-gray-500">
                            {item.last_name}
                          </td>
                          <td className="whitespace-break-words px-3 py-4 text-sm text-gray-500">
                            {item.email}
                          </td>
                          <td className="whitespace-break-words px-3 py-4 text-sm text-center">
                            {getStatusBadge(item.is_active)}
                          </td>
                          <td className="whitespace-break-words px-3 py-4 text-sm text-gray-500 text-right">
                            <UserActionDropdown
                              onViewDetails={() => handleExpandRow(item.id)}
                              onManageUser={() => handleManageUser(item.id)}
                            />
                          </td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {expandedRow && userDetails && (
          <UserDetailsDrawer
            details={formatUserDetails(userDetails)}
            onClose={() => {
              setExpandedRow(null);
              dispatch(setDrawer(false));
            }}
          />
        )}

        {!isLoading && sponsorMasterUsers?.items?.length > 0 && (
          <div className="mt-5">
            <Pagination
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              totalPages={sponsorMasterUsers?.total_pages || 1}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SponsorMasterUserList;
